import { Injectable } from "@angular/core";
import { ChartConfiguration } from "chart.js";
import { Observable } from "rxjs";

import { DataService } from "./dataservices/data.service";

export type DateType = "month" | "day" | "hour";
export type ChartFilterKey =
  | "flycut_count"
  // | "sentence_rewrite_count"
  | "paragraph_rewrite_count"
  // | "fly_grammar_accepted"
  // | "fly_grammar_autocorrect"
  | "fly_grammar_actions"
  | "characters_typed"
  | "cost_saved"
  | "time_saved"
  /**
   * FlyMSG Sentence Rewriter (issue #581). Unlike every other key this one is
   * NOT read from `v1/user/dashboard`'s `chart_data`; it is fed by
   * `getSentenceRewriteTrend()` and rendered as a two-series chart via
   * `prepareSentenceRewriteChartData()`. The option is only offered to users
   * entitled via `userHasSentenceRewriterAccess`.
   */
  | "sentence_rewriter";

/** Chart-filter key of the FlyMSG Sentence Rewriter series (see `ChartFilterKey`). */
export const SENTENCE_REWRITER_CHART_KEY: ChartFilterKey = "sentence_rewriter";

/** Label shown for the Sentence Rewriter chart option (flymsg-fe#561 naming ruling). */
export const SENTENCE_REWRITER_CHART_LABEL = "FlyMSG Sentence Rewriter";

/** `GET v2/sentence-rewrite/trend` clamps `months` to this range server-side. */
export const SENTENCE_REWRITE_TREND_MAX_MONTHS = 24;

/**
 * One zero-filled month of the personal Sentence Rewriter funnel, as returned
 * by `GET v2/sentence-rewrite/trend` (flymsg-be `SentenceRewriteTrendController`).
 * `suggestions_taken` = copied OR replaced — an upper bound on distinct
 * sentences until the extension threads a suggestion id (flymsg-be#776).
 */
export interface SentenceRewriteTrendMonth {
  /** Human label, e.g. "Jul 2026". */
  period: string;
  /** Sort key, e.g. "2026-07". */
  month: string;
  words_scored: number;
  suggestions_shown: number;
  suggestions_clicked: number;
  suggestions_copied: number;
  suggestions_replaced: number;
  suggestions_taken: number;
}

/**
 * Wire shape of `GET v2/sentence-rewrite/trend` as the FE receives it. The
 * controller returns `{ status, data: { months } }`, and flymsg-be's
 * `ProcessRequestResponseData` middleware (the `api` route group every
 * `romeo/api/*` route runs through) wraps that body as `{ result: <body> }`;
 * `DataService.extractData` hands it over untouched — the same reason the
 * dashboard reads `response.result` everywhere else.
 */
export interface SentenceRewriteTrendResponse {
  result: {
    status: string;
    data: { months: SentenceRewriteTrendMonth[] };
  };
}
export interface DateRange {
  text: string;
  id: number;
  differenceFromNow: number;
  dateType: DateType;
}

export interface ChartFilterOption {
  text: string;
  value: ChartFilterKey;
}

@Injectable({
  providedIn: "root",
})
export class StatisticsService {
  dateRanges: DateRange[] = [
    // { text: "Past 24 hours", id: 1, differenceFromNow: 24, dateType: "hour" },
    // { text: "Last 7 days", id: 2, differenceFromNow: 7, dateType: "day" },
    { text: "Last month", id: 3, differenceFromNow: 1, dateType: "month" },
    { text: "Last 3 months", id: 4, differenceFromNow: 3, dateType: "month" },
    { text: "Last 6 months", id: 5, differenceFromNow: 6, dateType: "month" },
    { text: "Last 9 months", id: 6, differenceFromNow: 9, dateType: "month" },
    { text: "Last 12 months", id: 7, differenceFromNow: 12, dateType: "month" },
    { text: "Last 24 months", id: 8, differenceFromNow: 24, dateType: "month" },
    { text: "Last 36 months", id: 9, differenceFromNow: 36, dateType: "month" },
    { text: "All Times", id: 10, differenceFromNow: -1, dateType: "month" },
  ];

  chartFilterOptions: ChartFilterOption[] = [
    { text: "Time Saved (hrs)", value: "time_saved" },
    { text: "FlyCuts Used", value: "flycut_count" },
    // { text: "Sentence Rewrite", value: "sentence_rewrite_count" },
    { text: "Paragraph Rewrite", value: "paragraph_rewrite_count" },
    { text: "Grammar and Spelling Fixes", value: "fly_grammar_actions" },
    // { text: "Spelling Auto Correct", value: "fly_grammar_autocorrect" },
    { text: "Cost Saved($)", value: "cost_saved" },
    { text: "Characters Typed", value: "characters_typed" },
  ];

  lineChartData: ChartConfiguration["data"] = {
    datasets: [
      {
        data: [],
        label: "",
        backgroundColor: "#F0DBFA",
        borderColor: "#5F177F",
        pointBackgroundColor: "#5F177F",
        fill: "origin",
      },
    ],
    labels: [],
  };

  adminLineChartOptions: ChartConfiguration["options"] = {
    elements: {
      line: {
        tension: 0.5,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          display: false,
        },
      },
      x: {
        grid: {
          display: false,
        },
      },
    },
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        position: "nearest",
        backgroundColor: "#2a1e36;",
      },
    },
  };

  constructor(private dataService: DataService) {}

  getStatistic(differenceFromNow: number = null, dateType: DateType = "month") {
    const url = differenceFromNow
      ? `v1/user/dashboard?differenceFromNow=${differenceFromNow}&dateType=${dateType}`
      : "v1/user/dashboard";
    return this.dataService.get({
      url: url,
      isLoader: false,
    });
  }

  /**
   * Personal FlyMSG Sentence Rewriter funnel for the calling user, one row per
   * month, zero-filled by the backend (issue #581 / flymsg-be#776).
   *
   * `differenceFromNow` is the dashboard date-range value (months; `-1` =
   * "All Times"). The endpoint clamps `months` to 1..24, so ranges wider than
   * that (36 months, All Times) are requested at the 24-month cap.
   */
  getSentenceRewriteTrend(differenceFromNow: number | null = null): Observable<SentenceRewriteTrendResponse> {
    const months = this.sentenceRewriteTrendMonths(differenceFromNow);
    // `ignoreErrors: true` is mandatory for background fetches (see
    // `AuthService.refreshUserFeatures`): without it `DataService.handleError`
    // hard-navigates the dashboard to `/session/error` on any 5xx from a
    // request whose failure the caller already swallows.
    return this.dataService.get({
      url: `v2/sentence-rewrite/trend?months=${months}`,
      isLoader: false,
      ignoreErrors: true,
    }) as Observable<SentenceRewriteTrendResponse>;
  }

  /** Maps a dashboard date-range value onto the endpoint's clamped `months` query param. */
  sentenceRewriteTrendMonths(differenceFromNow: number | null): number {
    if (differenceFromNow == null || differenceFromNow < 1) {
      return SENTENCE_REWRITE_TREND_MAX_MONTHS;
    }
    return Math.min(SENTENCE_REWRITE_TREND_MAX_MONTHS, Math.floor(differenceFromNow));
  }

  /** Sum of `suggestions_taken` across the months — the "Sentences Rewritten" tile value. */
  sumSentencesRewritten(months: SentenceRewriteTrendMonth[] | null | undefined): number {
    return (months ?? []).reduce((total, month) => total + (Number(month?.suggestions_taken) || 0), 0);
  }

  /**
   * Two-series (suggested vs corrected) chart data for the FlyMSG Sentence
   * Rewriter option. Every month the backend returns becomes a point — a
   * zero-activity month is charted as 0, never as a gap — and a missing or
   * non-numeric counter is coerced to 0 for the same reason. Returns a fresh
   * object each call (as does `prepareChartData`), so switching filters back
   * and forth can't leak the second dataset into the single-series chart.
   */
  prepareSentenceRewriteChartData(months: SentenceRewriteTrendMonth[] | null | undefined): ChartConfiguration["data"] {
    const rows = months ?? [];
    const asCount = (value: unknown) => Number(value) || 0;

    return {
      labels: rows.map((row) => row.period ?? row.month ?? ""),
      datasets: [
        {
          data: rows.map((row) => asCount(row.suggestions_shown)),
          label: "Sentences Suggested",
          backgroundColor: "#F0DBFA",
          borderColor: "#5F177F",
          pointBackgroundColor: "#5F177F",
          fill: "origin",
        },
        {
          data: rows.map((row) => asCount(row.suggestions_taken)),
          label: "Sentences Corrected",
          backgroundColor: "#FBE9DC",
          borderColor: "#C9611A",
          pointBackgroundColor: "#C9611A",
          fill: "origin",
        },
      ],
    };
  }

  filterStatistics(dateRangeId: number) {
    const { differenceFromNow, dateType } = this.dateRanges.find(({ id }) => id === dateRangeId);

    return { differenceFromNow, dateType };
  }

  prepareChartData(rawChartData: any, filterBy: ChartFilterKey = "time_saved"): ChartConfiguration["data"] {
    // Fresh object (and fresh `datasets` array) per call. ng2-charts 6
    // (`BaseChartDirective.ngOnChanges`) does `Object.assign(chart.config.data, newData)`
    // into whichever object the canvas was bound to FIRST, so a shared mutable
    // instance would have the two-series Sentence Rewriter `datasets` array
    // copied into it and every other chart would keep a phantom
    // "Sentences Corrected" series (flymsg-fe#583, QA S-12).
    const lineChartData: ChartConfiguration["data"] = {
      ...this.lineChartData,
      datasets: [{ ...this.lineChartData.datasets[0] }],
    };

    let newv = {};
    for (let month in rawChartData) {
      if (rawChartData[month] != undefined) {
        newv["" + month] = rawChartData[month][0] ?? rawChartData[month];
      }
    }

    if (newv) {
      rawChartData = newv;
    }

    if (!rawChartData) {
      return lineChartData;
    }

    lineChartData.labels = Object.keys(rawChartData);

    let dataset = [];
    lineChartData.labels.reverse();
    lineChartData?.labels.forEach((key: string) => {
      dataset.push(rawChartData[key][filterBy]);
      lineChartData.datasets[0].data = dataset;
    });

    let label = "";

    switch (filterBy) {
      case "flycut_count":
        label = "FlyCuts Expanded";
        break;
      // case "sentence_rewrite_count":
      //   label = "FlyCuts Expanded";
      //   break;
      case "paragraph_rewrite_count":
        label = "AI Paragraphs Rewrite";
        break;
      case "fly_grammar_actions":
        label = "Grammar and Spelling Fixes";
        break;
      // case "fly_grammar_accepted":
      //   label = "Grammar and Spelling Fixes";
      //   break;
      // case "fly_grammar_autocorrect":
      //   label = "Spelling Auto Correct";
      //   break;
      case "characters_typed":
        label = this.titleCase(filterBy);
        break;
      case "cost_saved":
        label = this.titleCase(filterBy) + " ($)";
        break;
      case "time_saved":
        label = this.titleCase(filterBy) + " (hrs)";
        break;

      default:
        break;
    }

    lineChartData.datasets[0].label = label;

    return lineChartData;
  }

  titleCase(s: string): string {
    return s.replace(/^[-_]*(.)/, (_, c) => c.toUpperCase()).replace(/[-_]+(.)/g, (_, c) => " " + c.toUpperCase());
  }
}
